import { sameFrameOwner, frameSequence } from '../contracts/frameDelivery.contract.js';
import { FRAME_DELIVERY_POLICY } from '../policies/frameDelivery.policy.js';
import { isFrameDisplayDelta } from './frameAdmission.service.js';

/** Scheduling only: no reducer, sequence advancement, snapshot authority or ACK success. */
export function createFrameDelivery({ enabled, clock, getOwner, getSequence, apply, recover,
  policy = FRAME_DELIVERY_POLICY }) {
  let queue = [], bytes = 0, frame = null, timer = null, disposed = false;
  let hiddenFallback = false, lastRecovery = -Infinity, draining = false, recoveryFlight = null;
  const pendingRecovery = new Map();
  let recoveryWake = null, recoveryEpoch = 0;
  const metrics = { queued: 0, applied: 0, stale: 0, barriers: 0, overflows: 0,
    highEvents: 0, highBytes: 0, maxWaitMs: 0, failed: 0, recoveries: 0, recoveryFailures: 0 };
  const current = item => sameFrameOwner(item.owner, getOwner(item.lane));
  const clearHandles = () => {
    if (frame !== null) clock.cancelFrame(frame);
    if (timer !== null) clock.cancel(timer);
    frame = timer = null;
  };
  const execute = item => {
    if (disposed || !current(item)) { metrics.stale++; return; }
    metrics.maxWaitMs = Math.max(metrics.maxWaitMs, clock.now() - item.received);
    apply(item.lane, item.envelope);
    metrics.applied++;
  };
  const clearRecoveryWake = () => {
    if (recoveryWake !== null) clock.cancel(recoveryWake);
    recoveryWake = null;
  };
  const pumpRecovery = () => {
    if (disposed) return;
    for (const [lane, intent] of pendingRecovery) if (!current(intent)) pendingRecovery.delete(lane);
    if (recoveryFlight) {
      // Retire stale intents even while the port is unresolved. A later ABA
      // transition must not revive an intent observed under a departed owner.
      if (!recoveryFlight.started) recoveryFlight.owners = recoveryFlight.owners.filter(current);
      return;
    }
    if (!pendingRecovery.size) { clearRecoveryWake(); return; }
    const remaining = policy.recoveryCooldownMs - (clock.now() - lastRecovery);
    if (remaining > 0) {
      if (recoveryWake === null) recoveryWake = clock.delay(() => {
        recoveryWake = null; pumpRecovery();
      }, remaining);
      return;
    }
    clearRecoveryWake();
    const owners = [...pendingRecovery.values()]; pendingRecovery.clear();
    lastRecovery = clock.now(); metrics.recoveries++;
    const flight = { epoch: recoveryEpoch, owners, started: false }; recoveryFlight = flight;
    // A flight stays owned until settlement; only new failed/overflow intent
    // can schedule a follow-up. A rejected recovery never retries itself.
    Promise.resolve().then(() => {
      const active = flight.owners.filter(current).map(item => item.lane);
      if (!disposed && flight.epoch === recoveryEpoch && active.length) {
        flight.started = true;
        return recover(active);
      }
    }).catch(() => { metrics.recoveryFailures++; }).finally(() => {
      if (recoveryFlight === flight) recoveryFlight = null;
      pumpRecovery();
    });
  };
  const requestRecovery = lanes => {
    if (disposed) return;
    for (const lane of lanes) {
      // Fixed lane cardinality; newest owner intent supersedes stale ownership.
      if (lane === 'list' || lane === 'fixture') pendingRecovery.set(lane, { lane, owner: getOwner(lane) });
    }
    pumpRecovery();
  };
  const drain = () => {
    if (draining) return;
    clearHandles();
    const pending = queue; queue = []; bytes = 0;
    const failed = [];
    draining = true;
    try {
      for (const item of pending) {
        try { execute(item); }
        catch { metrics.failed++; failed.push(item.lane); }
      }
    } finally { draining = false; }
    // An uncertain partial handler is never counted as applied; the remaining
    // prefix is still attempted in order, then the existing owner reconciles.
    requestRecovery(failed);
  };
  const overflow = (incoming = []) => {
    const lanes = [...new Set([...queue, ...incoming].filter(current).map(item => item.lane))];
    metrics.overflows++;
    hiddenFallback = !clock.visible();
    drain();
    requestRecovery(lanes);
  };
  const sync = () => {
    queue = queue.filter(entry => {
      if (current(entry)) return true;
      metrics.stale++; bytes -= entry.bytes; return false;
    });
    if (!queue.length) clearHandles();
    pumpRecovery();
  };
  const arm = () => {
    if (frame === null && clock.visible()) frame = clock.frame(drain);
    if (timer === null && queue.length) {
      const age = clock.visible() ? policy.visibleAgeMs : policy.hiddenAgeMs;
      timer = clock.delay(() => overflow(), Math.max(0, age - (clock.now() - queue[0].received)));
    }
  };
  return Object.freeze({
    sync,
    flush: drain,
    receive(lane, envelope) {
      if (disposed) return;
      const owner = getOwner(lane);
      const item = { lane, envelope, owner, received: clock.now() };
      if (envelope?.patch?.room && owner.room && envelope.patch.room !== owner.room) {
        metrics.stale++; return;
      }
      sync();
      const previous = [...queue].reverse().find(entry => entry.lane === lane);
      const sequence = frameSequence(envelope?.sequence);
      const since = previous?.envelope.sequence ?? getSequence(lane);
      const eligible = enabled && !hiddenFallback && sequence !== null && since !== null
        && owner.room === envelope?.patch?.room
        && sequence === since + 1 && isFrameDisplayDelta(lane, envelope);
      if (!eligible) { metrics.barriers++; drain(); execute(item); return; }
      // UTF-8 bytes are bounded above by three bytes per UTF-16 code unit.
      const size = JSON.stringify(envelope).length * 3;
      if (queue.length >= policy.maxEvents || bytes + size > policy.maxBytes) {
        overflow([item]); execute(item); return;
      }
      queue.push({ ...item, bytes: size }); bytes += size; metrics.queued++;
      metrics.highEvents = Math.max(metrics.highEvents, queue.length);
      metrics.highBytes = Math.max(metrics.highBytes, bytes);
      arm();
    },
    visibilityChanged() {
      if (disposed) return;
      hiddenFallback = false;
      // A visibility transition preserves order; it does not fabricate a new snapshot.
      drain();
    },
    cancel() { recoveryEpoch++; clearHandles(); clearRecoveryWake(); pendingRecovery.clear(); queue = []; bytes = 0; hiddenFallback = false; },
    close() { disposed = true; clearHandles(); clearRecoveryWake(); pendingRecovery.clear(); queue = []; bytes = 0; },
    inspect: () => ({ ...metrics, events: queue.length, bytes, hiddenFallback,
      recoveryPending: recoveryFlight !== null, recoveryIntents: pendingRecovery.size,
      handles: Number(frame !== null) + Number(timer !== null) + Number(recoveryWake !== null) }),
  });
}
